import { CSSProperties, Fragment, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { landingMedia } from './media';

const DEVICE_CYCLE_MS = 1200;
const DEVICE_RIM = '2px solid rgba(255, 255, 255, 0.3)';
const DEVICE_STAND = 'rgba(255, 255, 255, 0.3)';

function Screen({ on, radius }: { on: boolean; radius: number }) {
  return (
    <div style={{ width: '100%', height: '100%', borderRadius: radius, background: 'var(--screen)', overflow: 'hidden' }}>
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 'inherit',
          backgroundImage: `url('${landingMedia.deviceScreen}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: on ? 1 : 0,
          transform: on ? 'scale(1)' : 'scale(0.6)',
          transition: 'opacity var(--dur-reveal) var(--ease), transform var(--dur-reveal) var(--ease)',
        }}
      />
    </div>
  );
}

function Device({ on, label, children }: { on: boolean; label: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        opacity: on ? 1 : 0.4,
        transition: 'opacity var(--dur-slide) var(--ease)',
      }}
    >
      {children}
      <span className="label bright" style={{ fontSize: 'var(--fs-label)', letterSpacing: '0.2em' }}>
        {label}
      </span>
    </div>
  );
}

const bezel = (style: CSSProperties): CSSProperties => ({ border: DEVICE_RIM, background: 'var(--bg)', ...style });

export default function StoresSection() {
  const { t } = useTranslation('landing');
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setActive((i) => (i + 1) % 4), DEVICE_CYCLE_MS);
    return () => window.clearInterval(id);
  }, []);

  const stores: Array<[string, string, number]> = [
    [landingMedia.stores.appStore, 'App Store', 28],
    [landingMedia.stores.googlePlay, 'Google Play', 22],
    [landingMedia.stores.steam, 'Steam', 30],
  ];

  return (
    <section style={{ position: 'relative', padding: '60px var(--gutter) 90px', overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'var(--glow-section), var(--bg-2)',
          maskImage: 'linear-gradient(180deg, transparent 0%, #000 26%, #000 80%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 26%, #000 80%, transparent 100%)',
        }}
      />
      <div style={{ position: 'relative', textAlign: 'center' }}>
        <h2 className="t-h2" style={{ margin: '0 0 20px' }}>
          {t('stores.titleA', 'One game.')}
          <br />
          <span className="t-accent">{t('stores.titleB', 'Every store.')}</span>
        </h2>
        <p className="t-lede" style={{ margin: '0 auto 64px', maxWidth: 720 }}>
          {t(
            'stores.lede',
            'Entire builds native versions for iPhone, Android, Windows and Mac from the same project, then helps you get it into the App Store, Google Play and Steam. No porting, no rebuilding for each platform.',
          )}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'center', gap: 'clamp(24px, 4vw, 56px)' }}>
          <Device on={active === 0} label="iPhone">
            <div style={bezel({ width: 96, aspectRatio: '9 / 19', borderRadius: 20, padding: 5 })}>
              <Screen on={active === 0} radius={15} />
            </div>
          </Device>
          <Device on={active === 1} label="Android">
            <div style={bezel({ width: 100, aspectRatio: '9 / 19', borderRadius: 14, padding: 5 })}>
              <Screen on={active === 1} radius={9} />
            </div>
          </Device>
          <Device on={active === 2} label="Windows">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={bezel({ width: 260, aspectRatio: '16 / 10', borderRadius: 8, padding: 6 })}>
                <Screen on={active === 2} radius={3} />
              </div>
              <div style={{ width: 14, height: 26, background: 'rgba(255, 255, 255, 0.22)' }} />
              <div style={{ width: 90, height: 3, borderRadius: 2, background: DEVICE_STAND }} />
            </div>
          </Device>
          <Device on={active === 3} label="Mac">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={bezel({ width: 230, aspectRatio: '16 / 10', borderRadius: '10px 10px 2px 2px', padding: 6 })}>
                <Screen on={active === 3} radius={4} />
              </div>
              <div style={{ width: 280, maxWidth: '100%', height: 9, borderRadius: '0 0 8px 8px', background: DEVICE_STAND }} />
            </div>
          </Device>
        </div>
        <div style={{ marginTop: 56, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '16px 40px' }}>
          {stores.map(([src, name, height], i) => (
            <Fragment key={name}>
              {i > 0 && <span className="divider-v" />}
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--text-2)' }}>
                <img src={src} alt="" style={{ height, width: 'auto', objectFit: 'contain' }} />
                {name}
              </span>
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
